/**
 * MH-16 Nhật ký bản tin đã phát / nghe lại (mục 5.6 đặc tả giao diện), dựng theo
 * khuôn playLogView của bản mẫu CMS.
 *
 * Tra cứu lần phát theo địa bàn (ô chọn dạng cây, mặc định cả tỉnh), khoảng
 * ngày, loại bản tin, mức độ ưu tiên và lĩnh vực — đều lọc ở máy chủ
 * (`GET /api/v1/broadcast/logs`). Bấm "Nghe" mở trình phát của bản ghi tương
 * ứng. Với bản tin tiếp sóng, bản ghi là bản thu ở CẤP TỈNH — màn hình luôn nêu
 * rõ điều này để người nghe không hiểu sai là ghi âm tại từng thiết bị.
 *
 * Nút Xuất Excel của bản mẫu chưa có API ở máy chủ: hiện đúng vị trí và khoá.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viLichNhatKy } from '../ngon-ngu/vi-lich-nhat-ky';
import { viBienTapNoiDung } from '../ngon-ngu/vi-bien-tap';
import { viDanhSachLichPhat } from '../ngon-ngu/vi-danh-sach-lich-phat';
import { phatSongApi, type BoLocNgheLai, type DongNgheLai } from '../api/phat-song-api';
import { thietBiApi, type NutDiaBan, type TrangDuLieu } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TheTag, TruongLoc, type KieuTag } from '../thanh-phan/MauGiaoDien';
import { ChonDiaBanCay } from '../thanh-phan/ChonDiaBanCay';
import { hienThongBaoNhanh } from '../thanh-phan/ThongBaoNhanh';
import { ngayVeGiay } from './MH17LichTuan';
import './lich-nhat-ky.css';

const t = viLichNhatKy;
const tds = viDanhSachLichPhat;
const NHAN_LOAI_BAN_TIN: Record<string, string> = viBienTapNoiDung.loaiBanTinNhan;
const NHAN_UU_TIEN: Record<string, string> = viBienTapNoiDung.uuTienNhan;
const NHAN_LINH_VUC: Record<string, string> = vi.banTin.linhVucNhan;

export interface MH16Props {
  api?: Pick<typeof phatSongApi, 'ngheLai' | 'ngheLaiMot'> &
    Partial<Pick<typeof phatSongApi, 'xuatExcelNgheLai'>>;
  /** Tải cây địa bàn (tỉnh → xã); mặc định `thietBiApi.cayDiaBan`. */
  taiCayDiaBan?: (signal?: AbortSignal) => Promise<NutDiaBan[]>;
}

/** Đổi mã sang nhãn hiển thị; mã lạ thì hiện nguyên mã, không có thì "—". */
function nhanTheoMa(bang: Record<string, string>, ma: string | null | undefined): string {
  if (!ma) return '—';
  return bang[ma] ?? ma;
}

function nguonLaTiepSong(d: DongNgheLai): boolean {
  return d.nguonKichHoat === 'tiep_song' || d.nguonKichHoat === 'tiep_am';
}

const KIEU_KET_QUA: Record<string, KieuTag> = { thanh_cong: 'ok', loi: 'do', bi_dung: 'vang' };

function thoiLuong(d: DongNgheLai): string {
  // Ưu tiên thời lượng máy chủ trả về (HH:mm:ss); máy chủ cũ thì tính từ mốc phát.
  if (d.thoiLuong) return d.thoiLuong;
  if (d.thoiDiemKetThuc == null) return '—';
  const giay = Math.max(0, Math.round(d.thoiDiemKetThuc - d.thoiDiemBatDau));
  const gio = Math.floor(giay / 3600);
  const phut = String(Math.floor((giay % 3600) / 60)).padStart(2, '0');
  const s = String(giay % 60).padStart(2, '0');
  return gio > 0 ? `${gio}:${phut}:${s}` : `${phut}:${s}`;
}

function thoiGianNhatKy(giay: number): string {
  // Nhật ký hiển thị dạng HH:mm dd/MM/yyyy (LogTable của bộ quy chuẩn).
  const d = new Date(giay * 1000);
  const hm = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${hm} ${d.toLocaleDateString('vi-VN')}`;
}

export function MH16NgheLaiBanTin({ api, taiCayDiaBan }: MH16Props = {}): ReactNode {
  const goi = api ?? phatSongApi;
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState(SO_DONG_MAC_DINH);
  // '' = cả tỉnh (nút tỉnh của cây đại diện cho giá trị rỗng).
  const [nguonId, datNguonId] = useState('');
  const [loaiBanTin, datLoaiBanTin] = useState('');
  const [mucDoUuTien, datMucDoUuTien] = useState('');
  const [loaiLinhVuc, datLoaiLinhVuc] = useState('');
  const [tuThoiDiem, datTuThoiDiem] = useState('');
  const [denThoiDiem, datDenThoiDiem] = useState('');
  const [dongMo, datDongMo] = useState<DongNgheLai | null>(null);
  const [urlPhatLai, datUrlPhatLai] = useState<string | null>(null);
  const [dangMoPhatLai, datDangMoPhatLai] = useState(false);
  const [dangXuat, datDangXuat] = useState(false);

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => (taiCayDiaBan ? taiCayDiaBan(signal) : thietBiApi.cayDiaBan({}, signal)),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });
  const tenDiaBan = useMemo(
    () => new Map((cay.duLieu ?? []).map((d) => [d.maDinhDanh, d.ten])),
    [cay.duLieu],
  );

  const dangLoc =
    nguonId !== '' ||
    loaiBanTin !== '' ||
    mucDoUuTien !== '' ||
    loaiLinhVuc !== '' ||
    tuThoiDiem !== '' ||
    denThoiDiem !== '';

  /** Bộ lọc đang xem (không gồm phân trang) — dùng chung cho bảng và Xuất Excel. */
  const boLoc = (): BoLocNgheLai => {
    // Máy chủ so sánh với giây Unix; chuỗi ngày thô bị bỏ qua nên phải đổi trước.
    const tu = ngayVeGiay(tuThoiDiem);
    const den = ngayVeGiay(denThoiDiem, true);
    return {
      tuThoiDiem: tu === undefined ? undefined : String(tu),
      denThoiDiem: den === undefined ? undefined : String(den),
      nguonId: nguonId || undefined,
      loaiBanTin: loaiBanTin || undefined,
      mucDoUuTien: mucDoUuTien || undefined,
      loaiLinhVuc: loaiLinhVuc || undefined,
    };
  };

  const xuatExcel = async (): Promise<void> => {
    datDangXuat(true);
    try {
      const { soDong, tong } = await (goi.xuatExcelNgheLai ?? phatSongApi.xuatExcelNgheLai)(boLoc());
      hienThongBaoNhanh(
        soDong === 0
          ? t.ngheLai.xuatRong
          : tong > soDong
            ? dienVao(t.ngheLai.xuatVuotGioiHan, { tong, so: soDong })
            : dienVao(t.ngheLai.daXuatExcel, { so: soDong }),
        tong > soDong ? 'loi' : 'thanh-cong',
      );
    } catch (e) {
      hienThongBaoNhanh(
        dienVao(t.ngheLai.loiXuatExcel, { loi: e instanceof Error ? e.message : String(e) }),
        'loi',
      );
    } finally {
      datDangXuat(false);
    }
  };

  const kq = dungTaiDuLieu<TrangDuLieu<DongNgheLai>>({
    tai: (signal) => {
      return goi.ngheLai({ page: trang, pageSize: soDong, ...boLoc() } satisfies BoLocNgheLai, signal);
    },
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [trang, soDong, nguonId, loaiBanTin, mucDoUuTien, loaiLinhVuc, tuThoiDiem, denThoiDiem],
  });

  const moPhatLai = useCallback(
    async (d: DongNgheLai): Promise<void> => {
      datDongMo(d);
      datUrlPhatLai(null);
      datDangMoPhatLai(true);
      try {
        // API trả `url` (link tải ký mới); `duongDanFile` là link đã gửi thiết bị, có thể hết hạn.
        const kqBanGhi = (await goi.ngheLaiMot(d.id)) as {
          url?: string;
          urlNgheLai?: string;
          duongDanFile?: string;
        } | null;
        datUrlPhatLai(kqBanGhi?.url || kqBanGhi?.urlNgheLai || kqBanGhi?.duongDanFile || null);
      } catch {
        datUrlPhatLai(null);
      } finally {
        datDangMoPhatLai(false);
      }
    },
    [goi],
  );

  const c = t.ngheLai.cot;
  const cot: CotBang<DongNgheLai>[] = [
    {
      khoa: 'stt',
      nhan: c.stt,
      ve: (d) => <span>{(trang - 1) * soDong + (kq.duLieu?.items.indexOf(d) ?? 0) + 1}</span>,
    },
    {
      khoa: 'banTin',
      nhan: c.tenBanTin,
      ve: (d) => (
        <button type="button" className="nut nut--lien-ket" onClick={() => void moPhatLai(d)}>
          {d.tieuDe ?? d.banTinMa ?? vi.banTin.chuaCo}
        </button>
      ),
    },
    {
      khoa: 'thoiDiem',
      nhan: c.thoiGianPhat,
      ve: (d) => <span className="lnk-khong-xuong">{thoiGianNhatKy(d.thoiDiemBatDau)}</span>,
    },
    { khoa: 'thietBi', nhan: c.thietBiPhat, ve: (d) => <span>{d.cumLoaId}</span> },
    {
      khoa: 'diaBan',
      nhan: c.diaBanPhat,
      ve: (d) => <span>{d.tenDiaBan || (tenDiaBan.get(d.nguonId) ?? d.nguonId)}</span>,
    },
    {
      khoa: 'ketQua',
      nhan: c.trangThai,
      ve: (d) => (
        <span data-ket-qua={d.ketQua}>
          <TheTag kieu={KIEU_KET_QUA[d.ketQua] ?? 'trung-tinh'}>
            {t.ngheLai.ketQua[d.ketQua] ?? d.ketQua}
          </TheTag>
        </span>
      ),
    },
    {
      khoa: 'thoiLuong',
      nhan: c.thoiLuong,
      ve: (d) => <span data-cot="thoi-luong">{thoiLuong(d)}</span>,
    },
    {
      khoa: 'mucDoUuTien',
      nhan: c.mucDoUuTien,
      ve: (d) => <span data-cot="muc-do-uu-tien">{nhanTheoMa(NHAN_UU_TIEN, d.mucDoUuTien)}</span>,
    },
    {
      khoa: 'loaiBanTin',
      nhan: c.loaiBanTin,
      ve: (d) => <span data-cot="loai-ban-tin">{nhanTheoMa(NHAN_LOAI_BAN_TIN, d.loaiBanTin)}</span>,
    },
    {
      khoa: 'linhVuc',
      nhan: c.linhVuc,
      ve: (d) => <span>{nhanTheoMa(NHAN_LINH_VUC, d.loaiLinhVuc)}</span>,
    },
    {
      khoa: 'nguon',
      nhan: c.nguon,
      ve: (d) => <span>{t.ngheLai.nguonKichHoat[d.nguonKichHoat] ?? d.nguonKichHoat}</span>,
    },
    {
      khoa: 'nghe',
      nhan: c.nghe,
      ve: (d) => (
        <button
          type="button"
          className="nut nut--nho"
          data-hanh-dong="nghe-lai"
          aria-label={dienVao(t.ngheLai.ngheBanTin, { ten: d.tieuDe ?? d.banTinMa ?? d.id })}
          onClick={() => void moPhatLai(d)}
        >
          {t.ngheLai.nghe}
        </button>
      ),
    },
  ];

  const doiLoc = (dat: (v: string) => void) => (v: string) => {
    dat(v);
    datTrang(1);
  };

  return (
    <div className="man-hinh" data-man-hinh="MH-16">
      <div className="man-hinh__dau">
        <h1>{t.ngheLai.tieuDe}</h1>
      </div>

      <div className="lnk-loc" role="search">
        <TruongLoc nhan={t.chung.diaBan} htmlFor="nl-dia-ban">
          <ChonDiaBanCay
            id="nl-dia-ban"
            nhan={t.chung.diaBan}
            giaTri={nguonId}
            cay={cay.duLieu ?? []}
            nhanTatCa={tds.toanTinh}
            goiYTim={tds.timDiaBan}
            onDoi={doiLoc(datNguonId)}
          />
        </TruongLoc>
        <TruongLoc nhan={vi.banTin.tuNgay} htmlFor="nl-tu-ngay">
          <input
            id="nl-tu-ngay"
            type="date"
            value={tuThoiDiem}
            onChange={(e) => doiLoc(datTuThoiDiem)(e.target.value)}
          />
        </TruongLoc>
        <TruongLoc nhan={vi.banTin.denNgay} htmlFor="nl-den-ngay">
          <input
            id="nl-den-ngay"
            type="date"
            value={denThoiDiem}
            onChange={(e) => doiLoc(datDenThoiDiem)(e.target.value)}
          />
        </TruongLoc>
        <TruongLoc nhan={t.ngheLai.loaiBanTin} htmlFor="nl-loai">
          <select id="nl-loai" value={loaiBanTin} onChange={(e) => doiLoc(datLoaiBanTin)(e.target.value)}>
            <option value="">{t.ngheLai.chonLoaiBanTin}</option>
            {Object.entries(NHAN_LOAI_BAN_TIN).map(([ma, nhan]) => (
              <option key={ma} value={ma}>
                {nhan}
              </option>
            ))}
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.ngheLai.mucDoUuTien} htmlFor="nl-uu-tien">
          <select
            id="nl-uu-tien"
            value={mucDoUuTien}
            onChange={(e) => doiLoc(datMucDoUuTien)(e.target.value)}
          >
            <option value="">{t.ngheLai.chonMucDoUuTien}</option>
            {Object.entries(NHAN_UU_TIEN).map(([ma, nhan]) => (
              <option key={ma} value={ma}>
                {nhan}
              </option>
            ))}
          </select>
        </TruongLoc>
        <TruongLoc nhan={t.ngheLai.linhVuc} htmlFor="nl-linh-vuc">
          <select
            id="nl-linh-vuc"
            value={loaiLinhVuc}
            onChange={(e) => doiLoc(datLoaiLinhVuc)(e.target.value)}
          >
            <option value="">{t.ngheLai.chonLinhVuc}</option>
            {Object.entries(NHAN_LINH_VUC).map(([ma, nhan]) => (
              <option key={ma} value={ma}>
                {nhan}
              </option>
            ))}
          </select>
        </TruongLoc>
        <button
          type="button"
          className="nut nut--chinh lnk-nut-excel"
          data-hanh-dong="xuat-excel"
          disabled={dangXuat}
          aria-busy={dangXuat}
          onClick={() => void xuatExcel()}
        >
          {dangXuat ? t.ngheLai.dangXuatExcel : t.chung.xuatExcel}
        </button>
      </div>

      {dongMo ? (
        // Popup nghe lại: trình phát âm thanh gốc của trình duyệt (phát/tạm dừng, tua, âm lượng).
        <div className="man-che" role="presentation" onClick={() => datDongMo(null)}>
          <div
            className="hop-thoai nl-hop"
            data-vung="phat-lai"
            role="dialog"
            aria-modal="true"
            aria-label={dienVao(t.ngheLai.ngheBanTin, { ten: dongMo.tieuDe ?? dongMo.banTinMa ?? '' })}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="nl-hop__tieu-de">{dongMo.tieuDe ?? dongMo.banTinMa ?? vi.ngheLai.tieuDe}</h2>
            {nguonLaTiepSong(dongMo) ? <p data-vung="ghi-chu-tiep-am">{vi.ngheLai.ghiChuTiepAm}</p> : null}
            {dangMoPhatLai ? <p>{vi.trangThaiManHinh.dangTai}</p> : null}
            {!dangMoPhatLai && urlPhatLai ? (
              <audio className="nl-hop__audio" controls autoPlay preload="auto" src={urlPhatLai}>
                <track kind="captions" />
              </audio>
            ) : null}
            {!dangMoPhatLai && !urlPhatLai ? <p role="alert">{vi.ngheLai.khongCoAmThanh}</p> : null}
            <div className="nl-hop__nut">
              <button type="button" className="nut" onClick={() => datDongMo(null)}>
                {vi.ngheLai.dongY}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {kq.trangThai === 'co-du-lieu' ? (
        <div className="lnk-tong-tren" data-vung="tong-tren-bang">
          {dienVao(t.chung.tongBanGhi, { so: (kq.duLieu?.total ?? 0).toLocaleString('vi-VN') })}
        </div>
      ) : null}

      <BangDuLieu<DongNgheLai>
        nhan={t.ngheLai.tieuDe}
        cot={cot}
        dong={kq.duLieu?.items ?? []}
        khoaDong={(d) => d.id}
        nhanDong={(d) => d.tieuDe ?? d.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={() => {
          datNguonId('');
          datLoaiBanTin('');
          datMucDoUuTien('');
          datLoaiLinhVuc('');
          datTuThoiDiem('');
          datDenThoiDiem('');
          datTrang(1);
        }}
        moTaRong={vi.ngheLai.rongMoTa}
      />
    </div>
  );
}
